import { Star } from '@/shared/icons/phosphor'
import { type Component, For, createSignal } from 'solid-js'

export type RatingSize = 'sm' | 'md'

export interface RatingProps {
  value?: number | null | undefined
  onChange?: (value: number) => void
  max?: number
  readonly?: boolean
  size?: RatingSize
  label: string
  starLabel?: (value: number) => string
  class?: string
}

const sizeClasses: Record<RatingSize, string> = {
  sm: 'w-5 h-5',
  md: 'w-8 h-8',
}

export const Rating: Component<RatingProps> = (props) => {
  const [hovered, setHovered] = createSignal<number | null>(null)

  const max = () => props.max ?? 5
  const value = () => props.value ?? 0
  const shown = () => hovered() ?? value()
  const stars = () => Array.from({ length: max() }, (_, index) => index + 1)

  const star = (position: number) => (
    <Star
      class={sizeClasses[props.size ?? 'md']}
      weight={position <= shown() ? 'fill' : 'regular'}
      classList={{
        'text-[var(--color-secondary-600)]': position <= shown(),
        'text-[var(--color-neutral-500)]': position > shown(),
      }}
    />
  )

  if (props.readonly) {
    return (
      <span
        role="img"
        aria-label={props.label}
        class={`inline-flex items-center gap-1 ${props.class ?? ''}`}
      >
        <For each={stars()}>{(position) => star(position)}</For>
      </span>
    )
  }

  const move = (delta: number) =>
    props.onChange?.(Math.min(max(), Math.max(1, (value() || 1) + delta)))

  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      class={`-ml-1.5 flex items-center ${props.class ?? ''}`}
    >
      <For each={stars()}>
        {(position) => (
          <button
            type="button"
            role="radio"
            aria-checked={value() === position}
            aria-label={props.starLabel?.(position) ?? String(position)}
            tabindex={value() === position || (value() === 0 && position === 1) ? 0 : -1}
            class="rounded-[var(--radius-md)] p-1.5 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]"
            onClick={() => props.onChange?.(position)}
            onMouseEnter={() => setHovered(position)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(position)}
            onBlur={() => setHovered(null)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
                event.preventDefault()
                move(1)
              }
              if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
                event.preventDefault()
                move(-1)
              }
            }}
          >
            {star(position)}
          </button>
        )}
      </For>
    </div>
  )
}
